import { useMemo, useState } from 'react';
import { Container, makeStyles, useMediaQuery, useTheme } from '@material-ui/core';
import { useHistory, useLocation } from 'react-router-dom';
import { useGetCompetencesQuery, useGetCurrentUserQuery } from 'utils/wemble-api';
import organization from 'utils/organization';
import PageTitle from 'components/PageTitle';
import { useScrollRestore } from 'hooks/useScrollRestore';
import { buildTree, CategoryNode } from 'components/shared/UserCard/competenceTree';
import CategoryTree, { ALL, Selection, UNCATEGORIZED } from 'components/competences/manage/CategoryTree';
import CategoryDetail from 'components/competences/manage/CategoryDetail';
import { AddDialog, DialogModal, EditCategoryDialog } from 'components/competences/manage/CategoryDialogs';

const useStyles = makeStyles((theme) => ({
  container: {
    marginTop: 100,
    marginBottom: 65,
    [theme.breakpoints.down('sm')]: {
      marginTop: 16,
      padding: '0 12px',
    },
  },
  layout: {
    display: 'grid',
    gridTemplateColumns: '280px minmax(0, 1fr)',
    gap: 16,
    alignItems: 'start',
    marginTop: 12,
  },
}));

type Dialog =
  | { type: 'add'; kind: 'category' | 'competence'; parent: CategoryNode | null }
  | { type: 'edit-category'; node: CategoryNode };

/** Competencies, organised by category: a tree of every category at any depth
 *  beside the selected category's contents on desktop, a drill-down list on
 *  phones. The selection lives in the URL (?category=), so back and deep links
 *  work. Admins manage categories from the tree and the detail header. */
const Competences = () => {
  const classes = useStyles();
  const theme = useTheme();
  const narrow = useMediaQuery(theme.breakpoints.down('sm'));
  const history = useHistory();
  const location = useLocation();
  const [dialog, setDialog] = useState<Dialog | null>(null);

  const { data: user } = useGetCurrentUserQuery(undefined, { refetchOnMountOrArgChange: 20, refetchOnReconnect: true });
  const { data: competences } = useGetCompetencesQuery(undefined, {
    refetchOnMountOrArgChange: 15,
    refetchOnReconnect: true,
  });
  useScrollRestore('competences', !!user && !!competences);
  const org = organization(user?.company);

  const isAdmin = !!(user?.admin || user?.companyAdmin || user?.administratorPriviliges);
  // Per firm: admins only by default, everyone at legacy firms
  // (organization.ts `competencesEditableByEveryone`).
  const canManageCategories = isAdmin || !!org.competencesEditableByEveryone;
  const canEditCompetences = canManageCategories;

  const { tops, nodes, orphans } = useMemo(() => buildTree(competences ?? []), [competences]);
  const all = competences ?? [];

  const param = new URLSearchParams(location.search).get('category');
  const selection: Selection = param === UNCATEGORIZED ? UNCATEGORIZED : param && nodes.has(param) ? param : ALL;
  const node = selection && selection !== UNCATEGORIZED ? nodes.get(selection) : undefined;

  const select = (next: Selection) => {
    const search = next ? `?category=${encodeURIComponent(next)}` : '';
    if (search !== location.search) history.push(`/competences${search}`);
  };

  const close = () => setDialog(null);
  const total = all.filter((c) => !c.root).length;

  const detail = (
    <CategoryDetail
      // Remount per selection so search and checked rows start fresh.
      key={selection ?? 'all'}
      selection={selection}
      node={node}
      tops={tops}
      orphans={orphans}
      all={all}
      onSelect={select}
      canManageCategories={canManageCategories}
      canEditCompetences={canEditCompetences}
      onEditCategory={() => node && setDialog({ type: 'edit-category', node })}
      onCreateCategory={() => setDialog({ type: 'add', kind: 'category', parent: null })}
      onCreateCompetence={() => setDialog({ type: 'add', kind: 'competence', parent: node ?? null })}
    />
  );

  return (
    <Container className={classes.container}>
      <PageTitle title="Competencies" />
      {narrow ? (
        detail
      ) : (
        <div className={classes.layout}>
          <CategoryTree
            tops={tops}
            nodes={nodes}
            orphans={orphans.length}
            total={total}
            selected={selection}
            onSelect={select}
            onAddCategory={
              canManageCategories
                ? () => setDialog({ type: 'add', kind: 'category', parent: null })
                : undefined
            }
          />
          {detail}
        </div>
      )}

      <DialogModal open={!!dialog} onClose={close}>
        {dialog?.type === 'add' && (
          <AddDialog
            kind={dialog.kind}
            parent={dialog.parent}
            tops={tops}
            onClose={close}
            // A new category opens so you can fill it; a new competency just
            // appears in the list.
            onCreated={(id, kind) => kind === 'category' && select(id)}
          />
        )}
        {dialog?.type === 'edit-category' && (
          <EditCategoryDialog
            category={dialog.node}
            tops={tops}
            onClose={close}
            onDeleted={() => select(dialog.node.parent?.id ?? ALL)}
          />
        )}
      </DialogModal>
    </Container>
  );
};

export default Competences;
